<script setup lang="ts">
/**
 * ScholarTrip 2.0 Unified Header
 * 核心解決方案：
 * 1. 10 類產品智慧導覽，手機版自動轉為橫向滾動 (Horizontal Scroll)
 * 2. 支援 Page Meta 聯動的透明度轉換效果
 * 3. 學生模式 Smooth Toggle：即時觸發 Store 更新，不造成 Layout Shift
 */
import { useUserStore } from "~/stores/user/useUserStore";
defineProps<{ isTransparent: boolean; isScrolled: boolean }>();
const userStore = useUserStore();
const isMobileMenuOpen = ref(false);
// 10 種旅遊產品結構化定義 (BFF 路由對齊)
const productLines = [
  {
    id: "flights",
    label: "Flights",
    path: "/flights",
    icon: "ph:airplane-tilt-bold",
  },
  { id: "hotels", label: "Hotels", path: "/hotels", icon: "ph:buildings-bold" },
  {
    id: "packages",
    label: "Packages",
    path: "/packages",
    icon: "ph:suitcase-rolling-bold",
  },
  { id: "cars", label: "Cars", path: "/cars", icon: "ph:car-bold" },
  {
    id: "transfers",
    label: "Transfers",
    path: "/transfers",
    icon: "ph:taxi-bold",
  },
  { id: "cruises", label: "Cruises", path: "/cruises", icon: "ph:anchor-bold" },
  { id: "trains", label: "Trains", path: "/trains", icon: "ph:train-bold" },
  {
    id: "insurance",
    label: "Insurance",
    path: "/insurance",
    icon: "ph:shield-check-bold",
  },
  { id: "visas", label: "Visas", path: "/visas", icon: "gravity-ui:passport" },
  {
    id: "activities",
    label: "Activities",
    path: "/activities",
    icon: "ph:map-trifold-bold",
  },
];
const priorityProducts = productLines.slice(0, 5);
const moreProducts = productLines.slice(5);
// 監聽路由改變，自動關閉手機選單
const route = useRoute();
watch(
  () => route.path,
  () => (isMobileMenuOpen.value = false),
);
</script>
<template>
  <header
    class="fixed w-full top-0 z-[100] transition-all duration-500 ease-in-out"
    :class="isTransparent ? 'transparent-header' : 'glass-header'"
  >
    <div
      class="max-w-7xl mx-auto px-4 lg:px-8 flex justify-between items-center h-14 lg:h-18"
    >
      <!-- 1. Logo 區域 -->
      <NuxtLink to="/" class="shrink-0">
        <BaseAppLogo :is-light="isTransparent" />
      </NuxtLink>
      <!-- 2. 桌面版：產品導覽列 (Google Flights 風格) -->
      <nav class="hidden lg:flex items-center gap-1">
        <NuxtLink
          v-for="p in priorityProducts"
          :key="p.id"
          :to="p.path"
          class="px-5 py-2.5 rounded-full text-sm font-black transition-all flex items-center gap-2 hover:bg-black/5"
          active-class="bg-brand-600 !text-white shadow-lg shadow-brand-200"
        >
          <Icon :name="p.icon" class="text-lg" />
          {{ p.label }}
        </NuxtLink>
        <!-- 更多產品下拉選單 -->
        <div class="relative group ml-2">
          <button
            class="px-5 py-2.5 rounded-full text-sm font-black flex items-center gap-2 hover:bg-black/5 transition-colors"
          >
            More
            <Icon
              name="ph:caret-down-bold"
              class="text-xs transition-transform group-hover:rotate-180"
            />
          </button>
          <div
            class="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all transform translate-y-2 group-hover:translate-y-0"
          >
            <NuxtLink
              v-for="sub in moreProducts"
              :key="sub.id"
              :to="sub.path"
              class="block px-6 py-3.5 text-sm font-bold text-slate-600 hover:bg-brand-50 hover:text-brand-600 rounded-xl transition-colors"
            >
              {{ sub.label }}
            </NuxtLink>
          </div>
        </div>
      </nav>
      <!-- 3. 右側工具列 (學生模式開關 & 登入) -->
      <div class="flex items-center gap-3 lg:gap-6">
        <!-- 學生模式 Smooth Toggle (桌機與平板) -->
        <div
          class="hidden md:flex items-center gap-3 bg-slate-100/50 p-1.5 px-4 rounded-full border border-slate-200/50 cursor-pointer group"
          @click="userStore.toggleVerification()"
        >
          <span
            class="text-[10px] font-black uppercase text-slate-500 tracking-tighter"
            >Student Rates</span
          >
          <div
            class="w-10 h-5 shrink-0 rounded-full relative transition-colors duration-500 shadow-inner"
            :class="[userStore.isStudent ? 'bg-action-500' : 'bg-slate-300']"
          >
            <div
              class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-300 shadow-md"
              :class="{ 'translate-x-5': userStore.isStudent }"
            ></div>
          </div>
        </div>
        <button
          class="btn-primary !py-2 !px-6 !text-xs !rounded-full shadow-lg"
        >
          Sign In
        </button>
        <!-- 手機版選單觸發器 -->
        <button
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          class="lg:hidden p-2 text-2xl"
          :class="isTransparent ? 'text-white' : 'text-slate-900'"
        >
          <Icon :name="isMobileMenuOpen ? 'ph:x-bold' : 'ph:list-bold'" />
        </button>
      </div>
    </div>
    <!-- 4. 手機版全螢幕選單 (支援 10 種產品橫向滑動) -->
    <Transition name="slide-down">
      <div
        v-if="isMobileMenuOpen"
        class="lg:hidden bg-white border-t border-slate-100 absolute w-full shadow-2xl p-6 pt-8 pb-10"
      >
        <div class="flex overflow-x-auto hide-scrollbar gap-2 mb-8 snap-x">
          <NuxtLink
            v-for="p in productLines"
            :key="p.id"
            :to="p.path"
            class="shrink-0 px-6 py-3 rounded-2xl bg-slate-50 text-xs font-black uppercase tracking-widest border border-slate-100 snap-start active:bg-brand-50 transition-colors"
          >
            {{ p.label }}
          </NuxtLink>
        </div>
        <div class="space-y-3">
          <button
            class="w-full py-4 px-6 text-left font-black rounded-2xl bg-slate-50 text-slate-900"
          >
            Manage Bookings
          </button>
          <button
            class="w-full py-4 px-6 text-left font-black rounded-2xl bg-slate-50 text-slate-900"
          >
            Help Center
          </button>
          <div
            @click="userStore.toggleVerification()"
            class="w-full py-5 px-6 rounded-2xl border-2 border-brand-100 bg-brand-50 flex justify-between items-center transition-all"
          >
            <span
              class="font-black text-brand-700 uppercase text-xs tracking-widest"
              >Student Fares Locked</span
            >
            <Icon
              :name="
                userStore.isStudent
                  ? 'ph:toggle-right-fill'
                  : 'ph:toggle-left-fill'
              "
              class="text-3xl text-brand-600"
            />
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>
<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: scaleY(0.95);
}
</style>
